import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import Link from '@mui/material/Link';
import OwlIcon from '../../images/owl.svg';

// Little visual markers on the approval-chain sub-text so the reader
// can tell Tyto-side approvers (owl, same glyph as the top-left header
// logo) apart from government-side approvers (US flag emoji) at a
// glance. Kept inline so no new wrapper component is needed.
const OwlBadge = () => (
  <img
    src={OwlIcon}
    alt=""
    aria-hidden="true"
    style={{
      height: '1.15em',
      width: 'auto',
      verticalAlign: '-0.25em',
      marginRight: 4,
    }}
  />
);

export const imcsTarFormSchema = {
  api: {
    submit: 'tars',
  },
  payload: {
    includeRawFormData: true,
    // workflowType is not user-selectable (selector hidden). Force the fixed flow
    // into the payload here — payload.static fills it when missing/empty, which a
    // hidden field's `value` does not do. Backend requires workflowType.
    // TARs run the IMCS Standard Approval workflow: the two "IMCS TAR ..."
    // types were unbound from TAR by the v1.9.7.19 catalog change (PR #1802,
    // BU decision: Theater Director bypassed) — submitting them returns
    // 400 "not available for TAR in this program". Phase names are seeded
    // per environment; deliberately not repeated here.
    static: { workflowType: 'IMCS Standard Approval' },
    casts: {
      formVersionNumber: 'int',
      totalNTETripCost: 'number',
      dailyLodgingRate: 'number',
      dailyMieRate: 'number',
      mileageRate: 'number',
      milesDriving: 'int',
    },
    transform: (data, formData) => {
      const transformed = { ...data };
      const tarNumber = data.tarNumber || 'TAR-UNKNOWN';

      // Build single itinerary for the IMCS single-traveler form. travelerId
      // must be the traveler's contact UUID (travelerContactId from formData),
      // NOT the display name in fromContactId — the backend uuid.Parse()s it.
      // Omit the itinerary when the UUID is absent rather than send a bad value.
      const travelerContactId = formData?.travelerContactId;
      if (travelerContactId) {
        transformed.itineraries = [{
          name: `${tarNumber}-001`,
          travelerId: travelerContactId,
          legs: [],
        }];
      }

      // Build estimates from flat expenses
      if (Array.isArray(data.expenses)) {
        const lineItems = [];
        let counter = 1;

        data.expenses
          .filter(exp => !exp.__placeholder)
          .forEach(exp => {
            const days = parseInt(exp.numberOfDays) || 0;
            const unitCost = parseFloat(exp.itemUnitCost) || 0;
            const totalCost = parseFloat(exp.totalEstimatedCost) || 0;
            const finalTotal = totalCost || (days > 0 && unitCost > 0 ? parseFloat((days * unitCost).toFixed(2)) : 0);

            if (finalTotal > 0) {
              lineItems.push({
                lineItem: counter++,
                description: (exp.type || 'OTHER').toUpperCase(),
                unitCost: unitCost || finalTotal,
                days: days || 1,
                totalLineItemEstimate: finalTotal,
              });
            }
          });

        if (lineItems.length > 0) {
          transformed.estimates = [{
            estimateType: 'TAREstimate',
            objectName: `${tarNumber}-001`,
            lineItems,
            totalEstimate: lineItems.reduce((s, i) => s + i.totalLineItemEstimate, 0),
          }];
        }
      }

      // Clean up form-only fields
      delete transformed.expenses;

      // Strip empty strings from optional UUID fields so the backend
      // doesn't attempt uuid.Parse("") → "invalid UUID length: 0"
      const optionalUuidFields = ['projectId'];
      optionalUuidFields.forEach(k => {
        if (transformed[k] === '' || transformed[k] == null) {
          // Set to undefined (not delete) so the allow-list sees the key
          // via hasOwnProperty and doesn't fall back to formData's empty string
          transformed[k] = undefined;
        }
      });

      // Name of Traveler / TDY Location POC / Emergency Contact are now
      // free-text fields stored under the old UUID DTO names. Strip them
      // from the payload so the backend doesn't try to uuid.Parse() a
      // plain name string and reject the submission.
      transformed.fromContactId = undefined;
      transformed.tdyPocId = undefined;
      transformed.emergencyPocId = undefined;

      return transformed;
    },
    allow: [
      'clin',
      'contractorName',
      'dailyLodgingRate',
      'dailyMieRate',
      'description',
      'dueDate',
      'dutyLocation',
      'estimates',
      'eventEndDate',
      'eventLocation',
      'eventStartDate',
      'formId',
      'formVersionNumber',
      'itineraries',
      'lodgingZipCode',
      'mileageRate',
      'milesDriving',
      'numberOfNights',
      'numberOfTripDays',
      'popEnd',
      'popStart',
      'projectId',
      'projectName',
      'purposeJustification',
      'rawFormData',
      'returnDate',
      'subject',
      'tarContractCode',
      'tarDate',
      'taskOrderNumber',
      'totalNTETripCost',
      'travelDate',
      'workflowType',
    ],
  },
  blocks: [
    // Hidden values from forms API
    {
      type: 'hiddenApi',
      route: 'forms',
      filter: { formType: 'Trip Authorization Request (TAR) Form' },
      map: {
        formId: 'formId',
        formVersionNumber: 'versionNumber',
      },
    },
    // Hidden fields so formId/formVersionNumber are captured by flattenFields
    // and included in the payload output (not just formData fallback).
    // tarDate is hidden from the UI but seeded to "today" by IMCSTARForm
    // for new TARs and hydrated from the API for existing ones; it still
    // ships in the payload via the allow-list.
    { type: 'hidden', name: 'formId' },
    { type: 'hidden', name: 'formVersionNumber' },
    { type: 'hidden', name: 'tarDate' },

    // ===== TRAVELER DETAILS (first section) =====
    { type: 'heading', label: 'Traveler Details', variant: 'h6', icon: PersonOutlineIcon },

    // Row 1: Name of Traveler · Company, 50/50.
    {
      type: 'group',
      fields: [
        {
          // Name of Traveler is now auto-filled from the logged-in
          // user's display name (see IMCSTARForm effect that reads
          // meData.contact.name) — read-only so the traveler can't
          // change it.
          type: 'text',
          name: 'fromContactId',
          label: 'Name of Traveler',
          required: true,
          readOnly: true,
          flex: '1 1 300px',
        },
        {
          // Company is fixed — Tyto Athene is the only company that
          // submits TARs through this form. Read-only text field with
          // the value baked in so the data shape (contractorName) is
          // preserved without forcing the user to interact with it.
          type: 'text',
          name: 'contractorName',
          label: 'Company',
          required: true,
          readOnly: true,
          value: 'TYTO Athene',
          flex: '1 1 300px',
        },
      ],
    },

    // Row 2: Trip Start · Trip End · Duty Location, 1/3 width each.
    // Date span drives # of Nights / # of Days computation in row 3.
    // Weights: Trip Start ¼, Trip End ¼, Duty Location ½ — flex-grow
    // ratios 1 / 1 / 2. Duty Location's basis carries an extra +12px
    // (= FormBlock's inter-field gap) so its right edge aligns with
    // Company (row 1) and # of Days (row 3), which each only pay one
    // gap between their two fields; this row pays two.
    {
      type: 'group',
      fields: [
        { type: 'date', name: 'eventStartDate', label: 'Trip Start', requiredToSubmit: true, flex: '1 1 200px' },
        { type: 'date', name: 'eventEndDate', label: 'Trip End', requiredToSubmit: true, flex: '1 1 200px' },
        {
          type: 'text',
          name: 'dutyLocation',
          label: 'Duty Location',
          required: true,
          placeholder: 'Wiesbaden, Germany',
          flex: '2 1 412px',
        },
      ],
    },

    // Row 3: # of Nights · # of Days (auto-computed, read-only).
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'numberOfNights', label: '# of Nights', readOnly: true, flex: '1 1 200px' },
        { type: 'text', name: 'numberOfTripDays', label: '# of Days', readOnly: true, flex: '1 1 200px' },
      ],
    },

    // ===== SECTION 2: CONTRACT DETAILS — all values autopopulated, so
    // the whole section card is muted to communicate "look but don't
    // edit" at a glance. =====
    { type: 'heading', label: 'Contract Details', variant: 'h6', icon: AssignmentOutlinedIcon, muted: true },

    // Row 1: COR — hardcoded display field. Not in payload allow-list, so
    // this is form-display only; the backend will need the contact ID
    // wired through `toContactId` once Edgardo's contact UUID is known.
    // Row 1: COR + Contract, 50/50 (both hardcoded).
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'corName',
          label: 'COR',
          required: true,
          readOnly: true,
          value: 'Edgardo Edwards',
          flex: '1 1 300px',
        },
        {
          type: 'text',
          name: 'tarContractCode',
          label: 'Contract',
          required: true,
          readOnly: true,
          value: 'W52P1J-18-D-A127',
          flex: '1 1 300px',
        },
      ],
    },

    // Row 2: Task Order # + Project, 50/50. Task Order # is hardcoded;
    // Project is a live autocomplete against the /api/projects endpoint.
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'taskOrderNumber', label: 'Task Order #', required: true, readOnly: true, value: 'W519TC23F0241', flex: '1 1 300px' },
        {
          type: 'autocomplete',
          name: 'projectId',
          label: 'Project',
          required: true,
          placeholder: 'Search project',
          loadFromApiRoute: 'projects',
          loadFromApiField: 'projects',
          optionLabelKey: 'title',
          optionValueKey: 'projectId',
          filterBy: {
            field: 'workflowState',
            value: 'Approved',
            matchType: 'contains',
          },
          mapFields: { projectName: 'title' },
          flex: '1 1 300px',
        },
      ],
    },

    // Row 3: CLIN · PoP Start · PoP End — CLIN ½ width, PoP ¼ / ¼.
    // PoP dates hardcoded to the IMCS contract's performance window
    // (seeded in IMCSTARForm because getInitialValue returns null for
    // date fields regardless of schema `value`). CLIN basis carries an
    // extra +12px (= FormBlock's inter-field gap) so its right edge
    // aligns with the other 2-field / 3-field rows in the section.
    {
      type: 'group',
      fields: [
        {
          // CLIN dropdown — options load from GET /api/clins and are
          // client-filtered to codes starting with "0004" (the IMCS
          // program's active CLIN family; other CLINs on the contract
          // are managed by different programs and shouldn't be selectable
          // here). `filterOptions` is a schema-level hook added to
          // DynamicForm's fetch-completion path so we don't need a
          // backend query-param change.
          type: 'dropdown',
          name: 'clin',
          label: 'CLIN',
          required: true,
          placeholder: 'Select CLIN',
          loadFromApiRoute: 'clins',
          loadFromApiField: 'items',
          // Store + display the CLIN code (a short human string like
          // "0004AA"). CLIN doesn't have a dedicated backend column on
          // the TAR record — the value lives inside rawFormData — so
          // stashing the code (not the UUID) keeps the blob readable
          // and lets the ViewPanel render it without a second lookup.
          optionLabelKey: 'clinCode',
          optionValueKey: 'clinCode',
          // Match `0004` followed by at least one letter (e.g. 0004AA).
          // Excludes the bare "0004" placeholder — that's an invalid
          // billing target, so we don't want it selectable here.
          filterOptions: (item) =>
            typeof item?.clinCode === 'string' &&
            /^0004[A-Za-z]/.test(item.clinCode),
          flex: '2 1 412px',
        },
        { type: 'date', name: 'popStart', label: 'PoP Start', required: true, readOnly: true, flex: '1 1 200px' },
        { type: 'date', name: 'popEnd', label: 'PoP End', required: true, readOnly: true, flex: '1 1 200px' },
      ],
    },

    // ===== SECTION 2: CONTACT INFORMATION =====
    { type: 'heading', label: 'Contact Information', variant: 'h6', icon: ContactPhoneOutlinedIcon },

    // TDY Location POC — Name + Phone + Email on one row, equal thirds.
    // Phone and Email are captured in rawFormData (visible to the COR on
    // approval) but NOT in the payload allow-list, so the backend
    // doesn't try to store them as separate records.
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'tdyPocId',
          label: 'TDY Location POC Name',
          requiredToSubmit: true,
          placeholder: 'First & Last Name',
          flex: '1 1 200px',
        },
        {
          type: 'text',
          name: 'tdyPocPhone',
          label: 'TDY Location POC Phone Number',
          requiredToSubmit: true,
          placeholder: '+49 711 555 0123',
          flex: '1 1 200px',
        },
        {
          type: 'text',
          name: 'tdyPocEmail',
          label: 'TDY Location POC Email Address',
          requiredToSubmit: true,
          placeholder: 'name@example.com',
          flex: '1 1 200px',
        },
      ],
    },

    // Emergency Contact — Name + Phone + Email on one row, equal thirds.
    // Phone and Email are captured in rawFormData (visible to the COR on
    // approval) but NOT in the payload allow-list, so the backend
    // doesn't try to store them as separate records.
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'emergencyPocId',
          label: 'Emergency Contact Name',
          requiredToSubmit: true,
          placeholder: 'First & Last Name',
          flex: '1 1 200px',
        },
        {
          type: 'text',
          name: 'emergencyContactPhone',
          label: 'Emergency Contact Phone Number',
          requiredToSubmit: true,
          placeholder: '+1 555 555 0123',
          flex: '1 1 200px',
        },
        {
          type: 'text',
          name: 'emergencyContactEmail',
          label: 'Emergency Contact Email Address',
          requiredToSubmit: true,
          placeholder: 'name@example.com',
          flex: '1 1 200px',
        },
      ],
    },

    // ===== SECTION 3: EVENT, LOCATION, PER DIEM RATE & REMARKS =====
    {
      type: 'heading',
      label: 'Location Details',
      variant: 'h6',
      icon: PlaceOutlinedIcon,
      note: (
        <>
          Federal Lodging & M&IE Rates available at{' '}
          <Link
            href="https://www.travel.dod.mil/Travel-Transportation-Rates/Per-Diem/Per-Diem-Rate-Lookup/"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            tabIndex={-1}
          >
            travel.dod.mil/Per-Diem-Rate-Lookup
          </Link>
        </>
      ),
    },

    // Row 1: Location + Zip Code, equal 50/50.
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'eventLocation', label: 'Travel Destination', requiredToSubmit: true, placeholder: 'Clay Kaserne, Germany', flex: '1 1 300px' },
        { type: 'text', name: 'lodgingZipCode', label: 'Destination Postal Code', requiredToSubmit: true, placeholder: '65205', flex: '1 1 300px' },
      ],
    },

    // Row 2: Daily Lodging Rate + Daily M&IE Rate, equal 50/50.
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'dailyLodgingRate', label: 'Daily Lodging Rate', requiredToSubmit: true, placeholder: '247.00', dollars: true, flex: '1 1 300px' },
        { type: 'text', name: 'dailyMieRate', label: 'Daily M&IE Rate', requiredToSubmit: true, placeholder: '106.00', dollars: true, flex: '1 1 300px' },
      ],
    },

    // Row 3: Trip Remarks — full-width.
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'purposeJustification',
          label: 'Trip Remarks',
          requiredToSubmit: true,
          placeholder: 'Brief description for travel and any additional comments to support justification.',
          flex: '1 1 100%',
        },
      ],
    },

    // ===== SECTION 4: ESTIMATED EXPENSES =====
    { type: 'heading', label: 'Estimated Expenses', variant: 'h6', icon: ReceiptLongOutlinedIcon },

    {
      type: 'group',
      fields: [
        {
          type: 'expenseRows',
          name: 'expenses',
          label: 'Estimated Expenses',
          header: false,
          rows: [
            { type: 'airfare', label: 'Airfare', icon: 'airfare', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'baggageFees', label: 'Baggage Fees', icon: 'baggage', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'hotel', label: 'Lodging', icon: 'hotel', fields: ['numberOfDays', 'itemUnitCost', 'totalEstimatedCost'], fixed: true, quantityLabel: 'Nights' },
            { type: 'mealsIncidentals', label: 'Meals & Incidentals', icon: 'meals', fields: ['numberOfDays', 'itemUnitCost', 'totalEstimatedCost'], fixed: true, quantityLabel: 'Days' },
            { type: 'car', label: 'Rental Car', icon: 'car', fields: ['numberOfDays', 'itemUnitCost', 'totalEstimatedCost'], fixed: true, quantityLabel: 'Days' },
            { type: 'rentalFuel', label: 'Rental Fuel', icon: 'fuel', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'parking', label: 'Parking', icon: 'parking', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'povMileage', label: 'POV Mileage', icon: 'pov', fields: ['itemUnitCost', 'numberOfDays', 'totalEstimatedCost'], fixed: true, quantityLabel: 'Miles', mutedRate: true },
            { type: 'tolls', label: 'Tolls', icon: 'tolls', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'cabTrainSubway', label: 'Cab / Train / Subway', icon: 'transit', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'rideShare', label: 'Ride Share / Uber', icon: 'rideShare', fields: ['totalEstimatedCost'], fixed: true, editableTotal: true },
            { type: 'other', label: 'Other Expenses', icon: 'other', fields: ['expense', 'totalEstimatedCost'], fixed: false, editableTotal: true },
          ],
          fieldLabels: {
            expense: 'Description',
            numberOfDays: 'Qty / Days',
            itemUnitCost: 'Rate / Unit Cost',
            totalEstimatedCost: 'Estimated Cost',
          },
        },
      ],
    },

    // Total Estimated Trip Cost is rendered as a footer row inside the
    // ExpenseRows component (see ExpenseRows.jsx) so it sits inside the
    // same section card and aligns with the Estimated Cost column above.

    // ===== SECTION 5: SIGNATURES & APPROVAL CHAIN =====
    // Approval Due Date rides on the heading row (right-aligned) via
    // the heading's `headerField` slot — see FormBlock.jsx.
    {
      type: 'heading',
      label: 'Signatures & Approval Chain',
      variant: 'h6',
      icon: DrawOutlinedIcon,
      // Single approval chain — all TARs route through the same flow
      // now. Owl badges mark Tyto-side approvers; US-flag emojis mark
      // government approvers.
      subText: (
        <>
          <OwlBadge />Submitter → <OwlBadge />Program Administrator → 🇺🇸 APM → 🇺🇸 COR
        </>
      ),
      headerField: {
        type: 'date',
        name: 'dueDate',
        label: 'Approval Due Date',
        required: false,
        payloadTransform: (v) => (v && v !== '') ? v : undefined,
      },
    },

    // workflowType is no longer user-selectable. TARs run the IMCS Standard
    // Approval workflow; the two "IMCS TAR ..." types were unbound from TAR
    // by the v1.9.7.19 catalog change (PR #1802, BU decision: Theater
    // Director bypassed by design).
    { type: 'hidden', name: 'workflowType', value: 'IMCS Standard Approval' },

  ],
};
